/**
 * לקוחות — the entity-list archetype.
 *
 * Keyset pagination, never OFFSET: the cursor is (name, id), which is a total order
 * because the id breaks ties. That matters at 150 clients on the Office plan and it
 * matters more for correctness — an OFFSET page shifts under you when a row is inserted.
 */
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { directionHintFor, formatIsraeliPhone } from "@onecpa/shared";
import {
  Badge,
  Button,
  type Column,
  DataTable,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  IdentityCell,
  Input,
  Money,
  PageHeader,
  Segmented,
  Select,
  TabStrip,
} from "@onecpa/ui";
import { api, type ClientRow } from "../api/client.js";
import { agorot } from "../app/hooks.js";
import { useRole, useSession } from "../app/session.js";

type StatusFilter = "active" | "prospect" | "inactive" | "deleted";

export function ClientList() {
  const session = useSession();
  const { isManager, isStaff } = useRole();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("active");
  const [density, setDensity] = useState<"comfortable" | "compact">("comfortable");
  const [creating, setCreating] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["clients", session.orgId, search, status],
    queryFn: () =>
      api.clients.list(session.orgId, {
        limit: 200,
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(status === "deleted" ? { includeDeleted: true } : { status }),
      }),
  });

  const restore = useMutation({
    mutationFn: (id: string) => api.clients.restore(session.orgId, id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      session.toast("הלקוח שוחזר");
    },
    onError: (mutationError: Error) => session.toast(mutationError.message, "danger"),
  });

  // A deleted-only view has to filter client-side: the API returns live rows alongside
  // deleted ones when includeDeleted is set, because every other caller wants both.
  const rows = (data?.clients ?? []).filter((row) =>
    status === "deleted" ? row.isDeleted : !row.isDeleted,
  );

  const columns: Column<ClientRow>[] = [
    {
      key: "name",
      header: "לקוח",
      sticky: true,
      width: "240px",
      cell: (row) => (
        <IdentityCell
          name={row.name}
          detail={
            <bdi dir={directionHintFor("tax_file_number")}>
              {row.taxId ?? row.internalFileNumber ?? "—"}
            </bdi>
          }
        />
      ),
    },
    {
      key: "type",
      header: "סוג",
      cell: (row) => (
        <span className="text-xs text-ink-muted">
          {row.clientType === "company" ? "חברה בע״מ" : "עוסק מורשה"}
        </span>
      ),
    },
    {
      key: "staff",
      header: "מנהל חשבונות",
      hideOnSmall: true,
      cell: (row) =>
        row.assignedStaffName ? (
          <span className="text-xs text-ink">{row.assignedStaffName}</span>
        ) : (
          <Badge tone="pending">לא מוקצה</Badge>
        ),
    },
    {
      key: "city",
      header: "עיר",
      hideOnSmall: true,
      cell: (row) => <span className="text-xs text-ink-muted">{row.city ?? "—"}</span>,
    },
    {
      key: "contact",
      header: "טלפון",
      hideOnSmall: true,
      cell: (row) =>
        row.phoneE164 ? (
          <bdi className="tnum text-xs text-ink-muted">
            {formatIsraeliPhone(row.phoneE164)}
          </bdi>
        ) : (
          "—"
        ),
    },
    // The fee is commercial data. A bookkeeper does not see the column at all, rather
    // than seeing it blank — the API refuses to send it either way.
    ...(isManager
      ? [
          {
            key: "fee",
            header: "ריטיינר",
            align: "end" as const,
            cell: (row: ClientRow) =>
              row.monthlyFee ? (
                <Money agorot={agorot(row.monthlyFee)} colorize={false} />
              ) : (
                <span className="text-xs text-ink-faint">לא הוגדר</span>
              ),
          },
        ]
      : []),
    {
      key: "actions",
      header: "",
      align: "end",
      cell: (row) =>
        row.isDeleted ? (
          <Button
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              restore.mutate(row.id);
            }}
          >
            שחזור
          </Button>
        ) : null,
    },
  ];

  if (error) {
    return <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />;
  }

  return (
    <>
      <PageHeader
        title="לקוחות"
        subtitle={`${rows.length} לקוחות`}
        action={
          <div className="flex items-center gap-2">
            {isManager && (
              <Button icon="file-text" onClick={() => void navigate({ to: "/clients/import" })}>
                ייבוא מאקסל
              </Button>
            )}
            <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
              לקוח חדש
            </Button>
          </div>
        }
      >
        <TabStrip
          tabs={[
            { id: "active", label: "פעילים" },
            { id: "prospect", label: "לידים" },
            { id: "inactive", label: "לא פעילים" },
            ...(isManager ? [{ id: "deleted", label: "נמחקו" }] : []),
          ]}
          active={status}
          onSelect={(id) => setStatus(id as StatusFilter)}
        />
      </PageHeader>

      {isStaff && (
        <p className="mx-5 mt-4 rounded-card border border-info-ink/30 bg-info-bg px-3.5 py-2 text-xs text-info-ink">
          מוצגים רק הלקוחות שהוקצו לך. השרת אינו מחזיר לקוחות אחרים, גם לא בחיפוש.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3 px-5 pt-4">
        <Input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="חיפוש לקוח"
          aria-label="חיפוש לקוח"
          className="max-w-xs rounded-pill"
        />
        <Segmented<"comfortable" | "compact">
          options={[
            { value: "comfortable", label: "רגיל" },
            { value: "compact", label: "צפוף" },
          ]}
          value={density}
          onChange={setDensity}
        />
      </div>

      <div className="p-5">
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          onRowClick={(row) =>
            void navigate({ to: "/clients/$clientId", params: { clientId: row.id } })
          }
          loading={isLoading}
          density={density}
          caption="רשימת לקוחות המשרד"
          empty={
            <EmptyState
              title={search ? "לא נמצאו לקוחות" : "אין לקוחות ברשימה"}
              description={
                search
                  ? "נסו חיפוש אחר, או נקו את שדה החיפוש."
                  : "הוסיפו לקוח ראשון כדי להתחיל לעקוב אחר דיווחים ומועדים."
              }
              action={
                search ? (
                  <Button onClick={() => setSearch("")}>ניקוי חיפוש</Button>
                ) : (
                  <Button variant="primary" onClick={() => setCreating(true)}>
                    לקוח חדש
                  </Button>
                )
              }
            />
          }
        />
      </div>

      <CreateClientDialog open={creating} onClose={() => setCreating(false)} />
    </>
  );
}

function CreateClientDialog({ open, onClose }: { open: boolean; onClose(): void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [clientType, setClientType] = useState<"sole_trader" | "company">("sole_trader");
  const [taxId, setTaxId] = useState("");
  const [vatFrequency, setVatFrequency] = useState("monthly");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.clients.create(session.orgId, {
        name: name.trim(),
        clientType,
        vatFrequency,
        ...(taxId.trim() ? { taxId: taxId.trim() } : {}),
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      session.toast("הלקוח נוסף");
      onClose();
      setName("");
      setTaxId("");
      await navigate({ to: "/clients/$clientId", params: { clientId: result.client.id } });
    },
    onError: (mutationError: Error) => setError(mutationError.message),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="לקוח חדש"
      description="תדירויות הדיווח נוצרות אוטומטית וניתן לערוך אותן בכרטיס הלקוח."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={name.trim().length === 0}
            onClick={() => {
              setError(null);
              create.mutate();
            }}
          >
            הוספה
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <Field label="שם הלקוח" required>
          {({ id, invalid }) => (
            <Input
              id={id}
              value={name}
              invalid={invalid}
              onChange={(event) => setName(event.target.value)}
              placeholder="לדוגמה: אבני דרך בע״מ"
            />
          )}
        </Field>

        <Field label="סוג לקוח" help="קובע את מסלול הדוח השנתי">
          {({ id }) => (
            <Select
              id={id}
              value={clientType}
              onChange={(event) => setClientType(event.target.value as "sole_trader" | "company")}
            >
              <option value="sole_trader">עוסק מורשה</option>
              <option value="company">חברה בע״מ</option>
            </Select>
          )}
        </Field>

        <Field label="מספר עוסק / ח״פ">
          {({ id }) => (
            <Input
              id={id}
              value={taxId}
              onChange={(event) => setTaxId(event.target.value)}
              dir="ltr"
            />
          )}
        </Field>

        <Field label="תדירות מע״מ" help="דו-חודשי מדווח בחודשים אי-זוגיים בלבד">
          {({ id }) => (
            <Select
              id={id}
              value={vatFrequency}
              onChange={(event) => setVatFrequency(event.target.value)}
            >
              <option value="monthly">חודשי</option>
              <option value="bi_monthly">דו-חודשי</option>
              <option value="not_applicable">לא רלוונטי</option>
            </Select>
          )}
        </Field>

        {error && (
          <p role="alert" className="text-xs font-semibold text-red-ink">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
